import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  Translation,
  TRANSLOCO_LOADER,
  TranslocoService,
} from '@jsverse/transloco';
import {
  catchError,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  tap,
} from 'rxjs';
import {
  applyAliasFromTranslation,
  findBootTranslationBundle,
} from './boot-translations';
import { CatalogueModalTranslationService } from './catalogue-modal-translation.service';
import { clearI18nJsonFetchCache, fetchI18nJson } from './i18n-json-fetch';
import { ModelTranslationService } from './model-translation.service';
import {
  TranslocoHttpLoader,
  TranslocoPageRouteSyncService,
} from './transloco-loader';

/**
 * Swaps in freshly published translations without a page reload: refetches
 * every file this tab has merged, then re-renders views once.
 */
@Injectable({ providedIn: 'root' })
export class TranslationsReloadService {
  private readonly http = inject(HttpClient);
  private readonly transloco = inject(TranslocoService);
  // provideTransloco registers the loader with useClass, so the live instance is behind the token.
  private readonly loader = inject(TRANSLOCO_LOADER);
  private readonly pageRouteSync = inject(TranslocoPageRouteSyncService);
  private readonly modelTranslation = inject(ModelTranslationService);
  private readonly catalogueModalTranslation = inject(
    CatalogueModalTranslationService
  );

  async reload(): Promise<void> {
    clearI18nJsonFetchCache();

    await Promise.all([
      firstValueFrom(this.reloadLoaderPaths()),
      firstValueFrom(this.catalogueModalTranslation.reload()),
      this.modelTranslation.reload(),
    ]);
    // Page bundles go last: the catalogue modal re-merges the parent catalogues
    // bundle, which must not overwrite keys its child page bundles refine.
    await firstValueFrom(this.pageRouteSync.reloadLoadedBundles());

    // Every merge above is silent; one emit re-renders pipes and directives once
    // instead of rebuilding tables per file.
    this.transloco.setActiveLang(this.transloco.getActiveLang());
  }

  private reloadLoaderPaths(): Observable<void> {
    if (!(this.loader instanceof TranslocoHttpLoader)) {
      return of(undefined);
    }

    const reloads = this.loader
      .requestedPathList()
      .map(path => this.reloadLoaderPath(path));
    if (reloads.length === 0) {
      return of(undefined);
    }
    return forkJoin(reloads).pipe(map(() => undefined));
  }

  private reloadLoaderPath(path: string): Observable<void> {
    return fetchI18nJson(this.http, `${path}.json`).pipe(
      tap(translation => {
        // Same call Transloco makes after its own load, so scope aliases resolve alike.
        this.transloco.setTranslation(translation, path, {
          emitChange: false,
        });
        this.applyBootAlias(path, translation);
      }),
      map(() => undefined),
      // A file that fails keeps its previous strings.
      catchError(() => of(undefined))
    );
  }

  private applyBootAlias(path: string, translation: Translation): void {
    const langSeparator = path.lastIndexOf('/');
    const bundlePath = path.slice(0, langSeparator);
    const lang = path.slice(langSeparator + 1);

    const alias = findBootTranslationBundle(bundlePath)?.alias;
    if (!alias) {
      return;
    }
    applyAliasFromTranslation(this.transloco, translation, lang, alias, {
      emitChange: false,
    });
  }
}
